import { ComponentPropsWithoutRef, forwardRef, ReactNode, Ref } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { FloatingArrow, FloatingFocusManager, FloatingNode, FloatingPortal, useFloating } from '@floating-ui/react';
import { AnimatePresence, motion, MotionProps } from 'motion/react';
import { useThemeValues } from '../../../hooks';
import type { UsePopoverDialogResponse } from './use-popover-dialog';

type PopoverDialogArrowProps = Omit<ComponentPropsWithoutRef<typeof FloatingArrow>, 'context' | 'ref'>;

type Props = ReturnType<UsePopoverDialogResponse['getDialogProps']> & {
  children: ReactNode;
  layoutParent?: boolean | 'position' | 'size';
  css?: SerializedStyles | any;
  arrowProps?: PopoverDialogArrowProps;
  returnFocus?: boolean;
} & Pick<MotionProps, 'initial' | 'animate' | 'exit' | 'transition' | 'style'>;

const Dialog = forwardRef(
  (
    {
      isOpen,
      children,
      labelId,
      descriptionId,
      layoutParent,
      arrowProps,
      ...popoverProps
    }: Omit<Props, 'nodeId' | 'initialFocus'>,
    ref: Ref<HTMLDialogElement>
  ) => {
    const theme = useThemeValues();

    const { onAnimationStart, onDragStart, onDragEnd, onDrag, ...motionCompatibleProps } = popoverProps;
    return (
      <motion.dialog
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        ref={ref}
        layout={layoutParent}
        open={isOpen}
        css={css`
          border-radius: ${theme.borderRadius.medium};
          box-shadow: ${theme.shadows.heavy};
          border: none;
          background: white;
          :focus {
            outline: none;
          }
          z-index: ${theme.zIndex.popover};
        `}
        aria-describedby={descriptionId}
        aria-labelledby={labelId}
        {...motionCompatibleProps}
      >
        <FloatingArrow ref={popoverProps.arrowRef} context={popoverProps.context} {...arrowProps} />

        {children}
      </motion.dialog>
    );
  }
);

Dialog.displayName = 'PopoverDialogContent';

export const PopoverDialog = forwardRef(
  (
    { isOpen, nodeId, layoutParent, returnFocus = true, initialFocus, ...props }: Props,
    ref: Ref<HTMLDialogElement> | undefined
  ) => {
    useFloating({ nodeId: nodeId });

    return (
      <FloatingNode id={nodeId}>
        <FloatingPortal>
          <AnimatePresence>
            {isOpen && (
              <FloatingFocusManager context={props.context} initialFocus={initialFocus} returnFocus={returnFocus}>
                <Dialog {...props} isOpen={isOpen} ref={ref} />
              </FloatingFocusManager>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

PopoverDialog.displayName = 'PopoverDialog';
